import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { gr } from "../tokens";

// Underline tabs: 2px brand indicator, hairline under the list, no uppercase, optional counts
// (the count bubble has the class "GrTabs-count").
// tabs = [{ value, label, count?, id?, "aria-controls"? }]. MUI provides role=tablist/tab and arrow keys.
export default function GrTabs({ value, onChange, tabs, ariaLabel, sx }) {
  return (
    <Tabs
      value={value}
      onChange={onChange}
      aria-label={ariaLabel}
      variant="scrollable"
      scrollButtons={false}
      sx={{
        minHeight: gr.control.touch,
        borderBottom: `1px solid ${gr.color.line}`,
        "& .MuiTabs-indicator": {
          height: 2,
          borderRadius: gr.radius.pill,
          background: gr.color.brand,
        },
        "& .MuiTabs-flexContainer": { gap: gr.space[6] },
        ...sx,
      }}
    >
      {tabs.map(({ value: v, label, count, ...tabProps }) => (
        <Tab
          key={v}
          value={v}
          disableRipple
          label={
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: gr.space[2],
              }}
            >
              {label}
              {count !== undefined && count !== null ? (
                <Box
                  component="span"
                  className="GrTabs-count"
                  sx={{
                    minWidth: 20,
                    height: 20,
                    px: gr.space[1],
                    borderRadius: gr.radius.pill,
                    display: "inline-grid",
                    placeItems: "center",
                    fontSize: gr.font.size.xs,
                    fontWeight: gr.font.weight.semibold,
                    background: v === value ? gr.color.brandSoft : gr.color.bg,
                    color: v === value ? gr.color.brandDark : gr.color.muted,
                  }}
                >
                  {count}
                </Box>
              ) : null}
            </Box>
          }
          sx={{
            minHeight: gr.control.touch,
            minWidth: 0,
            px: 0,
            fontFamily: gr.font.family,
            fontSize: gr.font.size.md,
            fontWeight: gr.font.weight.medium,
            textTransform: "none",
            letterSpacing: 0,
            color: gr.color.ink2,
            "&:hover": { color: gr.color.ink },
            "&.Mui-selected": {
              color: gr.color.brandDark,
              fontWeight: gr.font.weight.semibold,
            },
            "&.Mui-focusVisible": {
              outline: `2px solid ${gr.color.brand}`,
              outlineOffset: 2,
              borderRadius: gr.radius.chip,
            },
          }}
          {...tabProps}
        />
      ))}
    </Tabs>
  );
}

GrTabs.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.node.isRequired,
      count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ).isRequired,
  ariaLabel: PropTypes.string,
  sx: PropTypes.object,
};
